import React from 'react';
import dynamic from 'next/dynamic';
import { BuildTabsProps, TabProps, productTypes, promotionTypes, products } from 'types';
import messages from '../../messages';
import { fixedTermIcon, overnightIcon, noticeAccountIcon } from './rebrandedIcons';

const OffersTable: any = dynamic(
  () => import('@raisin/cp-legacy-ui-lib').then((mod) => mod.OffersTable),
  {
    ssr: false,
  },
);

export const buildTabs = ({
  loading,
  offers,
  isRebranded,
  onInvestNowClick,
  onMobileInvestNowClick,
  locale,
  config,
  intl,
  t,
  ACTIVATED_TABS,
  isLoggedIn,
  onLoadMoreClickHandler,
  onOfferRowTabClickHandler,
  onTermFilterChangeHandler,
  onCountryFilterChangeHandler,
  onInvestmentAmountInputChangeHandler,
  onColumnSortChangeHandler,
  onCountryRatingChangeHandler,
  onCTAClickHandler,
  isShuffleProductsFeatureFlagOn,
}: BuildTabsProps): TabProps[] => {
  const tabsProps: TabProps[] = [];
  const tabsWidth = 'auto';
  const whiteSpace = 'normal';

  if (ACTIVATED_TABS.includes(productTypes.TERM_DEPOSIT)) {
    tabsProps.push({
      tabId: '0',
      tabName: t(messages.termDepositTabTitle),
      hint: t(messages.termDepositTabSubtitle),
      icon: 'lock-solid',
      rebrandedIcon: fixedTermIcon,
      style: { width: tabsWidth, whiteSpace },
      content: (
        <OffersTable
          locale={locale}
          loading={loading}
          config={{
            ...config,
            filter: {
              ...config.filter,
              type: productTypes.TERM_DEPOSIT,
              showOnlyFlexgeld: false,
              showForeignCurrency: false,
            },
            features: {
              ...config.features,
              showInvestmentAmountFilter: true,
            },
          }}
          offers={offers}
          intl={intl}
          onInvestNowClick={onInvestNowClick}
          onMobileInvestNowClick={onMobileInvestNowClick}
          isLoggedIn={isLoggedIn}
          onLoadMoreClickHandler={onLoadMoreClickHandler}
          onOfferRowTabClickHandler={onOfferRowTabClickHandler}
          onTermFilterChangeHandler={onTermFilterChangeHandler}
          onCountryFilterChangeHandler={onCountryFilterChangeHandler}
          onInvestmentAmountInputChangeHandler={onInvestmentAmountInputChangeHandler}
          onColumnSortChangeHandler={onColumnSortChangeHandler}
          onCountryRatingChangeHandler={onCountryRatingChangeHandler}
          onCTAClickHandler={onCTAClickHandler}
          isShuffleProductsFeatureFlagOn={isShuffleProductsFeatureFlagOn}
          isRebranded={isRebranded}
        />
      ),
      isRebranded,
    });
  }
  if (ACTIVATED_TABS.includes(productTypes.FLEXGELD)) {
    tabsProps.push({
      tabId: '1',
      tabName: t(messages.FlexgeldTabTitle),
      hint: t(messages.FlexgeldTabSubtitle),
      icon: 'flexgeld',
      style: { width: tabsWidth, whiteSpace },
      content: (
        <OffersTable
          locale={locale}
          loading={loading}
          config={config}
          offers={offers}
          intl={intl}
          onInvestNowClick={onInvestNowClick}
          onMobileInvestNowClick={onMobileInvestNowClick}
          isLoggedIn={isLoggedIn}
          onLoadMoreClickHandler={onLoadMoreClickHandler}
          onOfferRowTabClickHandler={onOfferRowTabClickHandler}
          onTermFilterChangeHandler={onTermFilterChangeHandler}
          onCountryFilterChangeHandler={onCountryFilterChangeHandler}
          onInvestmentAmountInputChangeHandler={onInvestmentAmountInputChangeHandler}
          onColumnSortChangeHandler={onColumnSortChangeHandler}
          onCountryRatingChangeHandler={onCountryRatingChangeHandler}
          onCTAClickHandler={onCTAClickHandler}
          isShuffleProductsFeatureFlagOn={isShuffleProductsFeatureFlagOn}
          isRebranded={isRebranded}
        />
      ),
    });
  }
  if (ACTIVATED_TABS.includes(productTypes.OVERNIGHT_DEPOSIT)) {
    tabsProps.push({
      tabId: '2',
      tabName: t(messages.OvernightTabTitle),
      hint: t(messages.OvernightTabSubtitle),
      icon: 'refresh',
      rebrandedIcon: overnightIcon,
      style: { width: tabsWidth, whiteSpace },
      content: (
        <OffersTable
          locale={locale}
          loading={loading}
          config={{
            ...config,
            filter: {
              ...config.filter,
              type: productTypes.OVERNIGHT_DEPOSIT,
            },
            features: {
              ...config.features,
              showSpecialNoticeAccountAsOvernight: false, // using marketed_as_product_type field instead
              showForeignCurrencyFilter: false,
              showFlexgeldFilter: false,
              splitNoticeAccountFromOvernightOffers: true,
              showInvestmentAmountFilter: true,
            },
          }}
          offers={offers}
          intl={intl}
          onInvestNowClick={onInvestNowClick}
          onMobileInvestNowClick={onMobileInvestNowClick}
          isLoggedIn={isLoggedIn}
          onLoadMoreClickHandler={onLoadMoreClickHandler}
          onOfferRowTabClickHandler={onOfferRowTabClickHandler}
          onTermFilterChangeHandler={onTermFilterChangeHandler}
          onCountryFilterChangeHandler={onCountryFilterChangeHandler}
          onInvestmentAmountInputChangeHandler={onInvestmentAmountInputChangeHandler}
          onColumnSortChangeHandler={onColumnSortChangeHandler}
          onCountryRatingChangeHandler={onCountryRatingChangeHandler}
          onCTAClickHandler={onCTAClickHandler}
          isShuffleProductsFeatureFlagOn={isShuffleProductsFeatureFlagOn}
          isRebranded={isRebranded}
        />
      ),
      isRebranded,
    });
  }
  if (ACTIVATED_TABS.includes(productTypes.NOTICE_ACCOUNT)) {
    tabsProps.push({
      tabId: '3',
      tabName: t(messages.noticeAccountTabTitle),
      hint: t(messages.noticeAccountTabSubtitle),
      icon: 'sync',
      rebrandedIcon: noticeAccountIcon,
      style: { width: tabsWidth, whiteSpace },
      content: (
        <OffersTable
          locale={locale}
          loading={loading}
          config={{
            ...config,
            filter: {
              ...config.filter,
              type: productTypes.NOTICE_ACCOUNT,
            },
            features: {
              ...config.features,
              showSpecialNoticeAccountAsOvernight: false, // using marketed_as_product_type field instead
              showFlexgeldFilter: false,
              showForeignCurrencyFilter: true,
              showTermFilter: false,
              showCountryCreditRatingFilter: false,
              showInvestmentAmountFilter: true,
            },
          }}
          offers={offers}
          intl={intl}
          onInvestNowClick={onInvestNowClick}
          onMobileInvestNowClick={onMobileInvestNowClick}
          isLoggedIn={isLoggedIn}
          onLoadMoreClickHandler={onLoadMoreClickHandler}
          onOfferRowTabClickHandler={onOfferRowTabClickHandler}
          onTermFilterChangeHandler={onTermFilterChangeHandler}
          onCountryFilterChangeHandler={onCountryFilterChangeHandler}
          onInvestmentAmountInputChangeHandler={onInvestmentAmountInputChangeHandler}
          onColumnSortChangeHandler={onColumnSortChangeHandler}
          onCountryRatingChangeHandler={onCountryRatingChangeHandler}
          onCTAClickHandler={onCTAClickHandler}
          isShuffleProductsFeatureFlagOn={isShuffleProductsFeatureFlagOn}
          isRebranded={isRebranded}
        />
      ),
      isRebranded,
    });
  }
  if (ACTIVATED_TABS.includes(promotionTypes.RAISIN_INVEST)) {
    tabsProps.push({
      tabId: '4',
      tabName: t(messages.raisinInvestTabTitle),
      style: { width: tabsWidth, whiteSpace },
      promotionTab: {
        title: t(messages.raisinInvestTabTitle),
        subtitle: t(messages.raisinInvestTabSubTitle),
        link: '/vermoegensverwaltung',
      },
    });
  }
  if (ACTIVATED_TABS.includes(promotionTypes.RAISIN_PENSION)) {
    tabsProps.push({
      tabId: '5',
      tabName: t(messages.raisinPensionTabTitle),
      style: { width: tabsWidth, whiteSpace },
      promotionTab: {
        title: t(messages.raisinPensionTabTitle),
        subtitle: t(messages.raisinPensionTabSubTitle),
        link: '/etf-ruerup',
      },
    });
  }

  return tabsProps;
};

/**
 * All Product Tabs for CP Offers Table.
 */
export const tabIds = new Map<products, string>([
  [productTypes.TERM_DEPOSIT, '0'],
  [productTypes.FLEXGELD, '1'],
  [productTypes.OVERNIGHT_DEPOSIT, '2'],
  [productTypes.NOTICE_ACCOUNT, '3'],
  [promotionTypes.RAISIN_INVEST, '4'],
  [promotionTypes.RAISIN_PENSION, '5'],
]);

export const getSelectedProductTab = (selectedTab: string) => {
  const product = Array.from(tabIds).find(([_key, value]) => value === selectedTab)![0];

  switch (product) {
    case productTypes.TERM_DEPOSIT:
      return 'TERM';
    case productTypes.FLEXGELD:
      return 'FLEXGELD';
    case productTypes.OVERNIGHT_DEPOSIT:
      return 'OVERNIGHT';
    case productTypes.NOTICE_ACCOUNT:
      return 'NOTICE';
    case promotionTypes.RAISIN_INVEST:
      return 'INVEST';
    case promotionTypes.RAISIN_PENSION:
      return 'PENSION';
    default:
      return '';
  }
};

export const selectDefaultTab = (ACTIVATED_TABS: productTypes[], DEFAULT_TAB: productTypes) => {
  const defaultTab = DEFAULT_TAB == null ? ACTIVATED_TABS[0] : DEFAULT_TAB;
  return tabIds.get(defaultTab);
};

export const getProductCategoryByType = (productType) => {
  const depositProducts = [
    productTypes.TERM_DEPOSIT,
    productTypes.FLEXGELD,
    productTypes.OVERNIGHT_DEPOSIT,
    productTypes.NOTICE_ACCOUNT,
  ];

  return depositProducts.includes(productType) ? 'deposit' : 'investment';
};
